import { useEffect, useState } from "react";
import {
  adminListCategories,
  adminCreateCategory,
  adminUpdateCategory,
  adminReorderCategories,
  adminDeleteCategory,
} from "@/lib/api";
import { useCatalog } from "@/context/CatalogContext";

const inputCls =
  "w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm";
const labelCls = "font-badge text-[10px] tracking-[0.22em] text-[#8A7568]";
const emptyForm = { name_pt: "", name_en: "", name_es: "", visible: true };
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const NameFields = ({ form, setForm, prefix }) => (
  <>
    {[["name_pt", "PORTUGUESE *"], ["name_en", "ENGLISH *"], ["name_es", "SPANISH (optional)"]].map(([k, l]) => (
      <label key={k} className="block">
        <span className={labelCls}>{l}</span>
        <input
          data-testid={`${prefix}-${k}`}
          value={form[k] || ""}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className={inputCls}
        />
      </label>
    ))}
  </>
);

const CreateCategory = ({ onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      await adminCreateCategory(form);
      setForm(emptyForm);
      onCreated();
    } catch (e) {
      setErr(errText(e, "Could not create category"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6" data-testid="admin-category-create">
      <div className="font-badge text-[10px] tracking-[0.22em] text-[var(--gl-orange-hover)]">CATEGORIES</div>
      <h2 className="font-serif-display text-2xl mt-1">Add a category</h2>
      <p className="text-xs text-[#8A7568] mt-1 mb-4">
        New categories appear in the menu, homepage tiles and footer as soon as they have at least one product in stock.
      </p>
      <div className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
        <NameFields form={form} setForm={setForm} prefix="admin-category-new" />
        <button
          data-testid="admin-category-create-btn"
          onClick={create}
          disabled={busy || !form.name_pt.trim() || !form.name_en.trim()}
          className="btn-orange text-xs disabled:opacity-40 whitespace-nowrap"
        >
          {busy ? "Adding…" : "Add category"}
        </button>
      </div>
      {err && <div data-testid="admin-category-create-error" className="mt-3 text-sm text-[#8B2E14]">{err}</div>}
    </section>
  );
};

const CategoryRow = ({ c, index, total, onMove, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(c);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, fallback) => {
    setBusy(true); setErr(null);
    try {
      await fn();
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(errText(e, fallback));
    } finally {
      setBusy(false);
    }
  };
  const payload = (over = {}) => ({ name_pt: form.name_pt, name_en: form.name_en, name_es: form.name_es, visible: c.visible, ...over });
  const remove = () => {
    if (window.confirm(`Delete category "${c.name_pt}"?`)) run(() => adminDeleteCategory(c.slug), "Delete failed");
  };
  const arrowCls = "w-8 h-8 rounded border border-[#F1DCC5] text-sm disabled:opacity-30 hover:bg-[#FFE9D6] transition-colors";
  const btnCls = "font-badge text-[10px] tracking-[0.18em] uppercase px-3 py-2 rounded border border-[#F1DCC5] hover:bg-[#FFE9D6] transition-colors disabled:opacity-40";

  return (
    <li data-testid={`admin-category-row-${c.slug}`} className="border border-[#F1DCC5] rounded-lg p-4 bg-white">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <button data-testid={`admin-category-up-${c.slug}`} disabled={index === 0} onClick={() => onMove(index, -1)} className={arrowCls}>↑</button>
          <button data-testid={`admin-category-down-${c.slug}`} disabled={index === total - 1} onClick={() => onMove(index, 1)} className={arrowCls}>↓</button>
        </div>
        <div className="flex-1 min-w-[180px]">
          <div data-testid={`admin-category-name-${c.slug}`} className="font-serif-display text-xl">{c.name_pt}</div>
          <div className="text-xs text-[#8A7568]">
            {c.name_en}{c.name_es ? ` · ${c.name_es}` : ""} · /collection/{c.slug}
          </div>
          <div className="text-xs mt-1">
            <span data-testid={`admin-category-count-${c.slug}`}>{c.product_count} product(s)</span>
            {c.visible === false && <span className="ml-2 px-2 py-0.5 rounded bg-[#241C18] text-white text-[10px] font-badge tracking-widest">HIDDEN</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid={`admin-category-edit-${c.slug}`} onClick={() => { setForm(c); setEditing(!editing); }} className={btnCls}>
            {editing ? "Close" : "Edit"}
          </button>
          <button
            data-testid={`admin-category-visibility-${c.slug}`}
            disabled={busy}
            onClick={() => run(() => adminUpdateCategory(c.slug, payload({ visible: c.visible === false })), "Update failed")}
            className={btnCls}
          >
            {c.visible === false ? "Show" : "Hide"}
          </button>
          <button
            data-testid={`admin-category-delete-${c.slug}`}
            disabled={busy || c.product_count > 0}
            title={c.product_count > 0 ? "Move its products to another category first" : ""}
            onClick={remove}
            className={`${btnCls} text-[#8B2E14]`}
          >
            Delete
          </button>
        </div>
      </div>
      {editing && (
        <div className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end mt-4">
          <NameFields form={form} setForm={setForm} prefix={`admin-category-edit-${c.slug}`} />
          <button
            data-testid={`admin-category-save-${c.slug}`}
            disabled={busy || !form.name_pt?.trim() || !form.name_en?.trim()}
            onClick={() => run(() => adminUpdateCategory(c.slug, payload()), "Save failed")}
            className="btn-orange text-xs disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
      {err && <div data-testid={`admin-category-error-${c.slug}`} className="mt-2 text-sm text-[#8B2E14]">{err}</div>}
    </li>
  );
};

const AdminCategories = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refreshCategories } = useCatalog();

  const load = async () => {
    try {
      setRows(await adminListCategories());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changed = () => { load(); refreshCategories(); };

  const move = async (i, dir) => {
    const next = [...rows];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setRows(next);
    try {
      await adminReorderCategories(next.map((c) => c.slug));
    } catch (_e) {
      window.alert("Could not save the new order, please try again.");
      load();
    }
    refreshCategories();
  };

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="admin-categories">
      <CreateCategory onCreated={changed} />
      <section>
        <h3 className="font-serif-display text-xl mb-1">Your categories</h3>
        <p className="text-xs text-[#8A7568] mb-4">
          Use the arrows to set the menu order. Hidden categories stay assigned to their products but disappear from the shop menus.
          A category can only be deleted once it has no products.
        </p>
        {loading ? (
          <div className="text-sm text-[#8A7568]">Loading…</div>
        ) : (
          <ul className="space-y-3" data-testid="admin-category-list">
            {rows.map((c, i) => (
              <CategoryRow key={`${c.slug}-${c.name_pt}-${c.visible}`} c={c} index={i} total={rows.length} onMove={move} onChanged={changed} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default AdminCategories;
